import React, { useEffect, useState, useRef } from 'react';
import { app } from '../lib/firebase';
import {
  getFirestore,
  doc,
  updateDoc,
  setDoc,
  getDoc,
} from 'firebase/firestore';
import { useUser } from '../context/UserProvider';
import { useTheme } from '../context/ThemeContext';
import { ChevronDown, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, changeLanguage, getCurrentLanguage } from '../i18n';

// ─── Currency Data ───
const CURRENCY_META = [
  ['EUR', '€', 'eu'], ['USD', '$', 'us'], ['GBP', '£', 'gb'], ['CAD', '$', 'ca'],
  ['AUD', '$', 'au'], ['CNY', '¥', 'cn'], ['KRW', '₩', 'kr'], ['JPY', '¥', 'jp'],
  ['RUB', '₽', 'ru'], ['SEK', 'KR', 'se'], ['DKK', 'KR', 'dk'], ['NOK', 'KR', 'no'],
  ['CHF', 'Fr.', 'ch'], ['PLN', 'zł', 'pl'], ['CZK', 'Kč', 'cz'],
  ['HRK', 'kn', 'hr'], ['RON', 'lei', 'ro'], ['TRY', '₺', 'tr'], ['INR', '₹', 'in'],
  ['ZAR', 'R', 'za'], ['BRL', 'R$', 'br'], ['MXN', '$', 'mx'], ['MYR', 'RM', 'my'],
  ['AED', 'د.إ', 'ae'], ['SAR', 'SR', 'sa'], ['KWD', 'KD', 'kw'], ['JOD', 'د.ا', 'jo'],
  ['EGP', 'E£', 'eg'], ['MAD', 'DH', 'ma'], ['DZD', 'دج', 'dz'], ['TND', 'DT', 'tn'],
  ['ETB', 'Br', 'et'], ['UZS', "so'm", 'uz'],
];

const clampPercent = (v) => Math.max(-100, Math.min(100, Number(v) || 0));
function normalizeCurrencyCode(value) {
  const code = String(value || 'USD').trim().toUpperCase();
  return code === 'BGN' ? 'EUR' : code || 'USD';
}

// ─── Themed Select Dropdown ───
function ThemedSelect({ value, options, onChange, renderOption, t, isForest, placeholder }) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const btnRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target) &&
        listRef.current && !listRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setDropUp(spaceBelow < 260 && rect.top > 260);
  }, [open]);

  const selected = options.find(o => o.value === value);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg transition-colors min-h-[42px]"
        style={{
          backgroundColor: isForest ? '#0f3a30' : t.input.background,
          border: `1px solid ${isForest ? 'rgba(255,255,255,0.12)' : t.input.border}`,
          color: isForest ? '#e8f5e9' : t.text.primary,
        }}
      >
        <span className="truncate flex items-center gap-2">
          {renderOption ? renderOption(selected) : (selected?.label || placeholder || value)}
        </span>
        <ChevronDown
          size={16}
          style={{ color: isForest ? '#a5d6a7' : t.text.tertiary, flexShrink: 0 }}
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className={`absolute z-50 w-full ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'} rounded-lg shadow-xl max-h-64 overflow-auto`}
          style={{
            backgroundColor: isForest ? '#0f3a30' : t.surface.card,
            border: `1px solid ${isForest ? 'rgba(255,255,255,0.12)' : t.surface.cardBorder}`,
          }}
        >
          {options.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className="px-3 py-2.5 flex items-center gap-2 cursor-pointer transition-colors"
              style={{
                backgroundColor: opt.value === value
                  ? (isForest ? 'rgba(76,175,80,0.2)' : 'rgba(76,175,80,0.1)')
                  : 'transparent',
                color: opt.value === value ? t.interactive.primary : (isForest ? '#e8f5e9' : t.text.primary),
              }}
              onMouseEnter={(e) => {
                if (opt.value !== value) {
                  e.currentTarget.style.backgroundColor = isForest ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = opt.value === value
                  ? (isForest ? 'rgba(76,175,80,0.2)' : 'rgba(76,175,80,0.1)')
                  : 'transparent';
              }}
            >
              {renderOption ? renderOption(opt) : opt.label}
              {opt.value === value && (
                <Check size={16} className="ml-auto" style={{ color: t.interactive.primary }} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Read-Only Field ───
function ReadOnlyField({ label, value, t, isForest }) {
  return (
    <div className="mb-3">
      <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
        {label}
      </label>
      <div
        className="px-3 py-2.5 rounded-lg min-h-[42px] flex items-center"
        style={{
          backgroundColor: isForest ? 'rgba(15,58,48,0.7)' : t.input.background,
          border: `1px solid ${isForest ? 'rgba(255,255,255,0.08)' : t.input.border}`,
          color: isForest ? '#e8f5e9' : t.text.primary,
        }}
      >
        {value || '—'}
      </div>
    </div>
  );
}

function prettyRole(code, roleOptions = [], roleListAvailable = false) {
  if (!code) return '';
  if (String(code).toLowerCase() === 'free') return 'Free';
  if (roleListAvailable && roleOptions.length > 0) {
    const match = roleOptions.find(opt => opt.code === code);
    if (match) return match.label;
  }
  return 'Premium';
}

// ─── Main Settings Component ───
export default function Settings() {
  const db = React.useMemo(() => getFirestore(app), []);
  const { ready, uid, settings, roleOptions, roleListAvailable } = useUser();
  const { themeMode, setTheme, theme: t, isForest } = useTheme();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [role, setRole] = useState('free');
  const [personalDiscount, setPersonalDiscount] = useState(0);
  const [discountText, setDiscountText] = useState('0');
  const [resetDiscount, setResetDiscount] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState(getCurrentLanguage());
  const { t: translate } = useTranslation();

  // Initialize form from settings
  useEffect(() => {
    if (!ready || !uid || !settings) return;
    setName(`${settings.firstName || ''} ${settings.lastName || ''}`.trim());
    setEmail(settings.email || '');
    setPhoneNumber(settings.phoneNumber || '');
    setCurrency(normalizeCurrencyCode(settings.currency));
    setRole(settings.discount || 'free');
    const pd = typeof settings.personalDiscount === 'number' ? settings.personalDiscount : 0;
    setPersonalDiscount(pd);
    setDiscountText(String(pd));
    setResetDiscount(!!settings.resetDiscount);
  }, [ready, uid, settings]);

  // Role options
  const selectableRoleOptions = React.useMemo(() => {
    const map = new Map();
    for (const opt of roleOptions || []) {
      if (opt?.code) map.set(opt.code, { code: opt.code, label: opt.label || opt.code });
    }
    if (!roleListAvailable && role && !map.has(role)) {
      const label = role.replace(/[-_]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, m => m.toUpperCase());
      map.set(role, { code: role, label });
    }
    return Array.from(map.values()).map(r => ({ value: r.code, label: r.label }));
  }, [roleOptions, role, roleListAvailable]);

  // Currency options
  const currencyOptions = CURRENCY_META.map(([code, sym, iso]) => ({
    value: code,
    label: `${sym} ${code}`,
    flag: iso,
  }));

  // Theme options
  const themeOptions = [
    { value: 'light', label: 'Light (Mint)' },
    { value: 'forest', label: 'Forest (Dark)' },
  ];

  // Reset on startup options
  const resetOptions = [
    { value: 'yes', label: translate('common.yes') },
    { value: 'no', label: translate('common.no') },
  ];

  // Language options
  const languageOptions = SUPPORTED_LANGUAGES.map(lang => ({
    value: lang.code,
    label: lang.nativeName,
  }));

  // Save handler
  const save = async () => {
    if (!uid || saving) return;
    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      const ref = doc(db, 'users', uid);
      const [firstName, ...rest] = (name || '').split(' ');
      const lastName = rest.join(' ');
      const clampedDiscount = clampPercent(personalDiscount);

      const snap = await getDoc(ref);
      const payload = {
        firstName: firstName || '',
        lastName: lastName || '',
        currency: normalizeCurrencyCode(currency),
        discount: role,
        personalDiscount: clampedDiscount,
        resetDiscount,
      };

      if (!snap.exists()) {
        await setDoc(ref, { ...payload, email, phoneNumber, selectedList: 'default', lists: {} });
      } else {
        await updateDoc(ref, payload);
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      setError(e.message || 'Failed to save');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (!ready) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" style={{ color: t.text.secondary }}>
        {translate('settings.loadingProfile')}
      </div>
    );
  }

  if (!uid) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" style={{ color: t.interactive.error }}>
        {translate('settings.signInRequired')}
      </div>
    );
  }

  return (
    <div className="max-w-xl pt-2">
      {/* Main Settings Card */}
      <div
        className="rounded-2xl p-5 md:p-6 shadow-lg"
        style={{
          backgroundColor: isForest ? '#0f3a30' : t.surface.card,
          border: `1px solid ${isForest ? 'rgba(255,255,255,0.12)' : t.surface.cardBorder}`,
          boxShadow: isForest ? '0 4px 12px rgba(0,0,0,0.3)' : `0 4px 12px ${t.shadow.md}`,
        }}
      >
        {/* Profile Section */}
        <h2
          className="text-lg font-semibold mb-4"
          style={{ color: isForest ? '#e8f5e9' : t.text.primary }}
        >
          {translate('settings.profile')}
        </h2>

        <ReadOnlyField label={translate('settings.name')} value={name} t={t} isForest={isForest} />
        {email && <ReadOnlyField label={translate('settings.email')} value={email} t={t} isForest={isForest} />}
        {phoneNumber && <ReadOnlyField label={translate('settings.phone')} value={phoneNumber} t={t} isForest={isForest} />}

        {/* Divider */}
        <div
          className="my-5"
          style={{
            height: 1,
            backgroundColor: isForest ? 'rgba(255,255,255,0.12)' : t.surface.cardBorder
          }}
        />

        {/* Preferences Section */}
        <h2
          className="text-lg font-semibold mb-4"
          style={{ color: isForest ? '#e8f5e9' : t.text.primary }}
        >
          {translate('settings.preferences')}
        </h2>

        {/* Language Dropdown */}
        <div className="mb-3">
          <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
            {translate('settings.language')}
          </label>
          <ThemedSelect
            value={language}
            options={languageOptions}
            onChange={(val) => {
              setLanguage(val);
              changeLanguage(val);
            }}
            t={t}
            isForest={isForest}
          />
        </div>

        {/* Role (if discountList available) */}
        {roleListAvailable && selectableRoleOptions.length > 0 ? (
          <div className="mb-3">
            <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
              {translate('settings.role')}
            </label>
            <ThemedSelect
              value={role}
              options={selectableRoleOptions}
              onChange={setRole}
              t={t}
              isForest={isForest}
            />
          </div>
        ) : (
          <ReadOnlyField
            label={translate('settings.role')}
            value={prettyRole(role, roleOptions, roleListAvailable)}
            t={t}
            isForest={isForest}
          />
        )}

        {/* Theme + Currency Row */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* Theme Dropdown */}
          <div>
            <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
              {translate('settings.theme')}
            </label>
            <ThemedSelect
              value={themeMode}
              options={themeOptions}
              onChange={setTheme}
              t={t}
              isForest={isForest}
            />
          </div>

          {/* Currency Dropdown */}
          <div>
            <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
              {translate('settings.currency')}
            </label>
            <ThemedSelect
              value={currency}
              options={currencyOptions}
              onChange={setCurrency}
              renderOption={(opt) => opt && (
                <span className="flex items-center gap-2">
                  <span className={`fi fi-${opt.flag} rounded-sm`} />
                  <span>{opt.label}</span>
                </span>
              )}
              t={t}
              isForest={isForest}
            />
          </div>
        </div>

        {/* Discount + Reset on Startup Row */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* Discount Input */}
          <div>
            <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
              {translate('settings.discountPercent')}
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={discountText}
              onChange={(e) => {
                const val = e.target.value.replace(/[^-0-9]/g, '');
                setDiscountText(val);
                const num = parseInt(val, 10);
                if (!isNaN(num)) setPersonalDiscount(clampPercent(num));
                else if (val === '' || val === '-') setPersonalDiscount(0);
              }}
              onBlur={() => {
                const clamped = clampPercent(personalDiscount);
                setPersonalDiscount(clamped);
                setDiscountText(String(clamped));
              }}
              className="w-full px-3 py-2.5 rounded-lg transition-colors min-h-[42px]"
              style={{
                backgroundColor: isForest ? '#0f3a30' : t.input.background,
                border: `1px solid ${isForest ? 'rgba(255,255,255,0.12)' : t.input.border}`,
                color: personalDiscount > 0 ? t.interactive.primary : personalDiscount < 0 ? t.interactive.error : (isForest ? '#e8f5e9' : t.text.primary),
              }}
            />
          </div>

          {/* Reset on Startup Dropdown */}
          <div>
            <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : t.text.secondary }}>
              {translate('settings.resetOnStartup')}
            </label>
            <ThemedSelect
              value={resetDiscount ? 'yes' : 'no'}
              options={resetOptions}
              onChange={(val) => setResetDiscount(val === 'yes')}
              t={t}
              isForest={isForest}
            />
          </div>
        </div>

        {/* Discount description */}
        <p className="text-xs mb-2" style={{ color: isForest ? '#a5d6a7' : t.text.tertiary }}>
          {translate('settings.discountDescription')}
        </p>
      </div>

      {/* Save Button */}
      <div className="mt-4 flex items-center gap-4 flex-wrap">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-6 py-2.5 rounded-lg font-medium transition-all"
          style={{
            backgroundColor: saving ? (isForest ? '#81c784' : t.text.secondary) : t.interactive.primary,
            color: '#ffffff',
            opacity: saving ? 0.7 : 1,
            cursor: saving ? 'wait' : 'pointer',
          }}
        >
          {saving ? translate('settings.saving') : translate('settings.saveSettings')}
        </button>

        {success && (
          <span className="text-sm" style={{ color: t.interactive.success }}>
            {translate('settings.settingsSaved')}
          </span>
        )}
        {error && (
          <span className="text-sm" style={{ color: t.interactive.error }}>
            {error}
          </span>
        )}
      </div>
    </div>
  );
}
